import { AxiosResponse } from 'axios';
import { BaseAPI } from './api-clients/base';
import { config } from '../libs/config/config';
import { getAxiosInstance, getBaseUrl } from './getAxiosInstance';
import {
  ApiConnectionError,
  ApiClientError,
  ApiServerError,
} from './errors';
import { AbortSignalCancelToken } from './AbortSignalCancelToken';
import {auth as firebaseAuth} from "../libs/firebase/firebase";

const isSuccessResponse = (status: number) => status >= 200 && status < 300;
const isClientErrorResponse = (status: number) => status >= 400 && status < 500;
const isServerErrorResponse = (status: number) => status >= 500;

async function parseResponse<Body>(response: AxiosResponse): Promise<Body> {
  if (isSuccessResponse(response.status)) {
    return response.data;
  }

  if (isClientErrorResponse(response.status)) {
    throw new ApiClientError(response.data, response.status);
  }

  if (isServerErrorResponse(response.status)) {
    throw new ApiServerError(response.data, response.status);
  }

  throw new ApiConnectionError(response);
}

type ApiCallFunction<Args extends unknown[], Body> = (
  ...args: Args
) => Promise<AxiosResponse<Body>>;

/**
 * openapi-generatorで生成されたAPIコールをラップする
 * @param ApiClass
 * @param apiCallFunction
 */
export function wrap<Args extends unknown[], Body>(
  ApiClass: typeof BaseAPI,
  apiCallFunction: ApiCallFunction<Args, Body>
) {
  return async (...args: Args) => {
    // abortSignalをcancelTokenに変換
    const options: any = args[args.length - 1];
    if (typeof options.abortSignal === 'object') {
      options.cancelToken = new AbortSignalCancelToken(options.abortSignal);
    }

    // ToDo: 時間に余裕がある時に認証ヘッダー情報を引数渡しに修正
    const idToken = await firebaseAuth.currentUser?.getIdToken();

    const pathName = await window.location.pathname;
    if (typeof pathName === "string") {
      const pathNames = pathName
        .replace(/^\/+/, "")
        .replace(/\/+$/, "")
        .split("/");
      if (config.task.pathNames.includes(pathNames[0]) && pathNames.length === 2) {
        options.headers = {
          ...options.headers,
          "x-roomId": pathNames[1]
        };
      }
    }

    // eslint-disable-next-line no-prototype-builtins
    if (options.hasOwnProperty('headers')) {
      // eslint-disable-next-line no-param-reassign
      options.headers = Object.assign(options.headers, { "Authorization": `Bearer ${idToken}` });
    } else {
      // eslint-disable-next-line no-param-reassign
      args[args.length - 1] = Object.assign(options, {
        headers: {
          "Authorization": `Bearer ${idToken}`
        }
      });
    }

    const instance = new ApiClass(undefined, getBaseUrl(), getAxiosInstance());

    const response = await apiCallFunction.call(instance, ...args);
    return parseResponse<Body>(response);
  };
}
